import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { equipmentCategories } from '../data/mock';
import Breadcrumbs from '../components/Breadcrumbs';
import EquipmentFilterSidebar from '../components/EquipmentFilterSidebar';
import { useEquipmentFilters } from '../hooks/useEquipmentFilters';
import { applyFilters } from '../lib/equipmentFilters';

const EquipmentDetail = ({ onQuoteClick }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scrollContainerRef = useRef(null);
  const { filters, toQueryString } = useEquipmentFilters();

  // On the SKU page, any filter change should redirect the user BACK to the
  // equipment listing with those filters applied (so they can browse results).
  const filterHandlersRedirect = (() => {
    const buildUrl = (overrides) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(overrides).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });
      const qs = next.toString();
      return `/equipment${qs ? `?${qs}` : ''}`;
    };
    return {
      setSearchQuery: (v) => navigate(buildUrl({ q: v })),
      setCategory: (v) =>
        navigate(buildUrl({ category: v === 'All' ? '' : v, type: '', range: '', pounds: '' })),
      setEquipmentType: (v) =>
        navigate(buildUrl({ type: v === 'All' ? '' : v, range: '', pounds: '' })),
      setRange: (v) => navigate(buildUrl({ range: v === 'All' ? '' : v })),
      setPounds: (v) => navigate(buildUrl({ pounds: v === 'All' ? '' : v })),
      setMaxPrice: (v) => navigate(buildUrl({ maxPrice: v })),
      setPriceTimeframe: (v) => navigate(buildUrl({ timeframe: v === 'daily' ? '' : v })),
      clear: () => navigate('/equipment'),
    };
  })();

  const equipment = equipmentCategories.find((item) => item.id === parseInt(id));

  // Dynamic brands from mock data (SKUs have 1-6 brands).
  const brands = equipment?.brands || [];
  const [selectedBrandIndex, setSelectedBrandIndex] = useState(0);
  // Reset selection if the SKU changes
  React.useEffect(() => {
    setSelectedBrandIndex(0);
  }, [id]);

  const activeBrand = brands[selectedBrandIndex] || null;
  const activePricing = activeBrand?.pricing ||
    equipment?.pricing || { daily: 0, weekly: 0, monthly: 0 };
  const activeImage = activeBrand?.image || equipment?.image;

  // Related equipment — driven by the sidebar filters, excluding current item
  const filteredPool = applyFilters(equipmentCategories, filters).filter(
    (item) => item.id !== parseInt(id)
  );
  // Fallback: if filters yield zero matches, show other items from the same category
  const relatedEquipment =
    filteredPool.length > 0
      ? filteredPool
      : equipmentCategories.filter(
          (item) => item.id !== parseInt(id) && item.category === equipment?.category
        );

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 350;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const goToSku = (skuId) => {
    navigate(`/equipment/${skuId}${toQueryString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!equipment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Equipment not found</h2>
          <Button
            onClick={() => navigate(`/equipment${toQueryString()}`)}
            className="bg-[#E28724] hover:bg-orange-600"
          >
            Back to Equipment
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="equipment-detail-page">
      <Breadcrumbs
        customItems={[
          { label: 'Equipment', href: `/equipment${toQueryString()}` },
          {
            label: equipment.category,
            href: `/equipment?category=${encodeURIComponent(equipment.category)}`,
          },
          { label: equipment.name },
        ]}
      />

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <button
          onClick={() => navigate(`/equipment${toQueryString()}`)}
          className="flex items-center gap-2 text-gray-700 hover:text-[#E28724] transition-colors group"
          data-testid="back-to-equipment-button"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-semibold">Back to Equipment</span>
        </button>
      </div>

      {/* Main Section with sidebar + details */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Floating filter sidebar — changing any filter redirects back to /equipment */}
            <div className="lg:w-64 flex-shrink-0">
              <EquipmentFilterSidebar filters={filters} handlers={filterHandlersRedirect} />
            </div>

            {/* Right content: image + brand selector + pricing */}
            <div className="flex-1">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                <div className="relative">
                  <div className="aspect-square rounded-2xl overflow-hidden bg-white shadow-2xl border border-gray-100">
                    <img
                      src={activeImage}
                      alt={activeBrand ? `${activeBrand.name} ${activeBrand.model}` : equipment.name}
                      className="w-full h-full object-contain p-6 transition-opacity duration-300"
                      key={activeImage}
                      data-testid="equipment-detail-image"
                    />
                  </div>
                  {activeBrand && (
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border-l-4 border-[#E28724]">
                      <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                        {activeBrand.name}
                      </div>
                      <div className="text-sm font-bold text-[#1a2332]">{activeBrand.model}</div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">
                    {equipment.name}
                  </h1>

                  {brands.length > 0 && (
                    <div className="mb-8">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Select Brand &amp; Model
                        {brands.length > 1 && (
                          <span className="ml-2 text-xs text-gray-500 font-normal">
                            ({brands.length} available)
                          </span>
                        )}
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {brands.map((b, idx) => {
                          const active = idx === selectedBrandIndex;
                          return (
                            <button
                              key={`${b.name}-${b.model}-${idx}`}
                              onClick={() => setSelectedBrandIndex(idx)}
                              className={`px-4 py-3 rounded-lg font-semibold transition-all text-left min-w-[140px] flex-1 sm:flex-none ${
                                active
                                  ? 'bg-[#E28724] text-white shadow-lg scale-[1.02]'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              data-testid={`brand-select-${idx}`}
                            >
                              <div className="text-xs opacity-80 uppercase tracking-wide">
                                {b.name}
                              </div>
                              <div className="text-sm font-bold">{b.model}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 mb-6 border-2 border-[#E28724]">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      Rental Pricing
                      {activeBrand && (
                        <span className="text-base font-medium text-gray-600 ml-2">
                          — {activeBrand.name} {activeBrand.model}
                        </span>
                      )}
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center bg-white rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1 font-medium">Daily</div>
                        <div className="text-2xl font-black text-[#E28724]" data-testid="pricing-daily">
                          ${activePricing.daily}
                        </div>
                      </div>
                      <div className="text-center bg-white rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1 font-medium">Weekly</div>
                        <div className="text-2xl font-black text-[#E28724]" data-testid="pricing-weekly">
                          ${activePricing.weekly}
                        </div>
                      </div>
                      <div className="text-center bg-white rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1 font-medium">Monthly</div>
                        <div className="text-2xl font-black text-[#E28724]" data-testid="pricing-monthly">
                          ${activePricing.monthly}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mt-4 text-center font-medium">
                      ✓ Includes delivery, pickup, and damage waiver
                    </p>
                  </div>

                  {/* Spec Sheet — changes per selected brand */}
                  {activeBrand && activeBrand.specs && Object.keys(activeBrand.specs).length > 0 && (
                    <div
                      className="bg-white rounded-xl p-6 mb-6 border-2 border-gray-200"
                      data-testid="spec-sheet"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">Spec Sheet</h3>
                        <span className="text-xs font-semibold text-[#E28724] uppercase tracking-wider bg-orange-50 px-3 py-1 rounded-full border border-orange-200">
                          {activeBrand.name} {activeBrand.model}
                        </span>
                      </div>
                      <dl className="divide-y divide-gray-100">
                        {Object.entries(activeBrand.specs).map(([key, value]) => (
                          <div
                            key={key}
                            className="py-3 flex justify-between items-baseline gap-4"
                            data-testid={`spec-row-${key.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <dt className="text-sm text-gray-600 font-medium">{key}</dt>
                            <dd className="text-sm text-[#1a2332] font-bold text-right">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}

                  <Button
                    onClick={onQuoteClick}
                    size="lg"
                    className="w-full bg-[#E28724] hover:bg-orange-600 text-white font-bold py-6 text-lg shadow-lg"
                    data-testid="detail-request-quote-button"
                  >
                    Request Quote for This Equipment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is this equipment used for? */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What is this equipment used for?
          </h2>
          <div className="bg-white rounded-xl p-8 shadow-md" data-testid="equipment-used-for">
            <p className="text-gray-700 text-lg leading-relaxed">
              {equipment.seoDescription ||
                'Professional-grade equipment designed for maximum efficiency and reliability across construction, maintenance, and installation projects.'}
            </p>
          </div>
        </div>
      </section>

      {/* Related / filtered equipment rail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                {filteredPool.length > 0 ? 'Matching Equipment' : 'Related Equipment'}
              </h2>
              <p className="text-gray-600 mt-1">
                {filteredPool.length > 0
                  ? `${filteredPool.length} item${filteredPool.length === 1 ? '' : 's'} match your filters`
                  : 'You may also be interested in these'}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => scroll('left')}
                className="p-3 rounded-full bg-gray-100 hover:bg-[#E28724] hover:text-white transition-all shadow-md"
                aria-label="Scroll left"
                data-testid="related-scroll-left"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="p-3 rounded-full bg-gray-100 hover:bg-[#E28724] hover:text-white transition-all shadow-md"
                aria-label="Scroll right"
                data-testid="related-scroll-right"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
            data-testid="related-equipment-rail"
          >
            {relatedEquipment.map((item) => (
              <Card
                key={item.id}
                className="flex-shrink-0 w-80 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 hover:border-[#E28724] cursor-pointer"
                onClick={() => goToSku(item.id)}
                data-testid={`related-card-${item.id}`}
              >
                <div className="relative h-64 overflow-hidden bg-white">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-contain p-3 transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                    <h3 className="text-xl font-bold mb-1 line-clamp-2">{item.name}</h3>
                    {item.pricing && (
                      <p className="text-orange-300 font-semibold">
                        Starting at ${item.pricing.daily}/day
                      </p>
                    )}
                  </div>
                </div>

                <CardContent className="p-4">
                  {(() => {
                    const specs = item.brands?.[0]?.specs;
                    if (!specs) return null;
                    const priorityKeys = [
                      'Work Height', 'Working Height', 'Platform Height',
                      'Lift Capacity', 'Max Lift Height',
                      'Horizontal Outreach', 'Max Forward Reach',
                      'Platform Capacity', 'Operating Weight', 'Power',
                    ];
                    const entries = [];
                    for (const key of priorityKeys) {
                      if (specs[key] && entries.length < 3) {
                        entries.push([key, specs[key]]);
                      }
                    }
                    return (
                      <ul className="mb-4 space-y-1" data-testid={`related-specs-${item.id}`}>
                        {entries.map(([k, v]) => (
                          <li key={k} className="flex justify-between text-xs border-b border-gray-100 pb-1">
                            <span className="text-gray-500 font-medium">{k}</span>
                            <span className="text-[#1a2332] font-semibold text-right">{v}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                  <Button
                    className="w-full bg-[#E28724] hover:bg-orange-600 text-white font-semibold"
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSku(item.id);
                    }}
                  >
                    View Equipment
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}

            {/* Browse All card */}
            <Card className="flex-shrink-0 w-80 border-2 border-dashed border-gray-300 hover:border-[#E28724] transition-all duration-300 hover:shadow-xl">
              <CardContent className="h-full flex flex-col items-center justify-center p-8 min-h-[400px]">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <ArrowRight className="w-10 h-10 text-[#E28724]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Browse All Equipment
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  See our complete catalog of 400+ equipment options
                </p>
                <Button
                  onClick={() => navigate(`/equipment${toQueryString()}`)}
                  size="lg"
                  className="bg-[#E28724] hover:bg-orange-600 text-white font-semibold"
                >
                  View All
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            ← Scroll horizontally to view more equipment →
          </p>
        </div>
      </section>
    </div>
  );
};

export default EquipmentDetail;
